import { CallControls } from '@backoffice-frontend/ad-operator-call'
import {
  AdOperatorReportIoActionItem,
  useAdOperatorReportIo,
} from '@backoffice-frontend/ad-operator-report-io'
import { AdPoolingToggle } from '@backoffice-frontend/ad-pooling-toggle'
import {
  FccAdAlertType,
  FccServiceCancellationTypeEnum,
} from '@backoffice-frontend/graphql'
import { useTimers } from '@backoffice-frontend/hooks'
import { H6, VerticalDivider } from '@backoffice-frontend/patterns'
import { CreationContext } from '@backoffice-frontend/report-io-lib'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { BoumRight } from '@moia-dev/moia-token-claims'
import {
  ForbiddenAltIcon,
  ReloadIcon,
  RouteEndIcon,
  XIcon,
} from '@moia-dev/pace-icons'
import {
  ActionItem,
  ActionItemDivider,
  ActionMenu,
  Button,
  IconButton,
  LoadingIndicator,
  Tag,
  useActionMenu,
} from '@moia-dev/pace-web'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdVehicleStream } from '../../AdVehicleStream/useAdVehicleStream'
import { ServiceCancellationInitiatedAction } from '../../ServiceCancellation/ServiceCancellationProvider'
import { useServiceCancellation } from '../../ServiceCancellation/useServiceCancellation'
import { NotImplemented } from '../NotImplemented'
import type { AdVehicleStreamHeaderVehicleFragment } from './AdVehicleStreamHeader.hook'
import { EmergencyButton } from './EmergencyButton'

export const AdVehicleStreamHeader = ({
  vehicle,
  onClose,
}: {
  vehicle: AdVehicleStreamHeaderVehicleFragment
  onClose: VoidFunction
}) => {
  const { t } = useTranslation()
  const { pendingUpdate } = useAdVehicleStream()
  const { openReportDialog } = useAdOperatorReportIo()
  const { setTimer } = useTimers()
  const [isServiceActionsOpen, setIsServiceActionsOpen] = useState(false)

  const {
    state: serviceCancellationState,
    initiate: initiateServiceCancellation,
    initiatedAction,
    isTripManagementAllowed,
  } = useServiceCancellation()

  const hasPermissionToEndTripsEarly = useUserHasAccess({
    requiredRights: [BoumRight.FLEET_CONTROL_CANCEL_TRIP],
  })

  const {
    getReferenceProps: getServiceActionsReferenceProps,
    ...serviceActionsProps
  } = useActionMenu({
    isOpen: isServiceActionsOpen,
    setIsOpen: setIsServiceActionsOpen,
    placement: 'bottom-end',
  })

  useEffect(() => {
    if (serviceCancellationState.matches('confirmed')) {
      setIsServiceActionsOpen(false)
    }
  }, [serviceCancellationState])

  const highestPriorityAlert = vehicle.fccOperatorAdAlerts?.alerts.at(0)
  const disableEmergencyButton =
    highestPriorityAlert?.type === FccAdAlertType.EmergencyStop

  const handlePoolingTurnedOff = () => {
    setTimer(
      'openReportProblemDialog',
      () =>
        openReportDialog({
          vehicleId: vehicle.id,
          creationContext: CreationContext.FleetMapTurnPoolingOff,
        }),
      1000,
    )
  }

  return (
    <div
      css={theme => ({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.space.Space3,
        justifyContent: 'space-between',
        backgroundColor: theme.semantic.ColorSurfaceDefault,
        borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
        paddingTop: theme.space.Space2,
        paddingBottom: theme.space.Space2,
      })}
    >
      <div
        css={{
          display: 'flex',
        }}
      >
        <div
          css={theme => ({
            display: 'flex',
            paddingLeft: theme.space.Space6,
            paddingRight: theme.space.Space4,
            flexDirection: 'column',
            justifyContent: 'center',
          })}
        >
          <p
            css={theme => ({
              fontSize: '12px',
              color: theme.semantic.ColorContentReduced,
            })}
          >
            Vehicle Stream
          </p>
          <div
            css={theme => ({
              display: 'flex',
              gap: theme.space.Space2,
            })}
          >
            <H6 css={{ whiteSpace: 'nowrap' }}>{vehicle.label}</H6>
            {vehicle.isSelfDriving && <Tag label="AD" />}
          </div>
        </div>
        <VerticalDivider />
        <CallControls vehicle={vehicle} />
        <VerticalDivider />
      </div>
      <div
        css={{
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            padding: `${theme.space.Space0} ${theme.space.Space2}`,
          })}
        >
          <AdPoolingToggle
            vehicle={vehicle}
            showTitle={false}
            onTurnedOff={handlePoolingTurnedOff}
          />
        </div>
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            padding: `${theme.space.Space0} ${theme.space.Space2}`,
          })}
        >
          <NotImplemented>
            <EmergencyButton disabled={disableEmergencyButton} />
          </NotImplemented>
        </div>
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            padding: `${theme.space.Space0} ${theme.space.Space2}`,
          })}
        >
          <Button
            {...getServiceActionsReferenceProps()}
            ref={serviceActionsProps.refs.setReference}
            label={t('adFleetMap_actions_label')}
            hasDropdown
            variant={'secondary'}
            onClick={() => {
              setIsServiceActionsOpen(prevState => !prevState)
            }}
          />
          <ActionMenu {...serviceActionsProps}>
            <AdOperatorReportIoActionItem
              vehicleId={vehicle.id}
              onClick={() => setIsServiceActionsOpen(false)}
            />
            <ActionItem
              label={t('common_restoreMission_button')}
              leading={
                serviceCancellationState.matches('initiated') &&
                initiatedAction ===
                  ServiceCancellationInitiatedAction.RestoreMission ? (
                  <LoadingIndicator />
                ) : (
                  <ReloadIcon />
                )
              }
              onClick={() =>
                initiateServiceCancellation(
                  vehicle.id,
                  FccServiceCancellationTypeEnum.AllTrips,
                  ServiceCancellationInitiatedAction.RestoreMission,
                )
              }
              disabled={!serviceCancellationState.matches('idle')}
            />
            {hasPermissionToEndTripsEarly && (
              <ActionItem
                label={t(
                  'adFleetMap_vehicleStreamActionMenuManageTrips_button',
                )}
                leading={
                  serviceCancellationState.matches('initiated') &&
                  initiatedAction ===
                    ServiceCancellationInitiatedAction.TripManagement ? (
                    <LoadingIndicator />
                  ) : (
                    <RouteEndIcon />
                  )
                }
                onClick={() =>
                  initiateServiceCancellation(
                    vehicle.id,
                    FccServiceCancellationTypeEnum.SomeTrips,
                    ServiceCancellationInitiatedAction.TripManagement,
                  )
                }
                disabled={!isTripManagementAllowed}
              />
            )}
            <ActionItemDivider />
            <ActionItem
              critical
              label={t('adFleetMap_serviceActions_cancelService_button')}
              leading={
                serviceCancellationState.matches('initiated') &&
                initiatedAction ===
                  ServiceCancellationInitiatedAction.CancelService ? (
                  <LoadingIndicator />
                ) : (
                  <ForbiddenAltIcon color="var(--color-critical-default)" />
                )
              }
              onClick={() =>
                initiateServiceCancellation(
                  vehicle.id,
                  FccServiceCancellationTypeEnum.AllTrips,
                  ServiceCancellationInitiatedAction.CancelService,
                )
              }
              disabled={!serviceCancellationState.matches('idle')}
            />
          </ActionMenu>
        </div>
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            paddingRight: theme.space.Space4,
          })}
        >
          <IconButton
            icon={pendingUpdate ? <LoadingIndicator /> : <XIcon />}
            variant="plain"
            onClick={onClose}
            disabled={pendingUpdate}
          />
        </div>
      </div>
    </div>
  )
}
